import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import "./tradegpt.scss";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  finchatMessageCancel,
  finchatMessageLoad,
} from "../redux/slices/finchatSlice";
import {
  PostMessagesFunction,
  FinchatMessagePayload,
  MessageAttachments,
  RootState,
  sendMessageFunction,
  MainLayoutType,
} from "../assets/interfaces/interfaces";
import ChatWindow from "./ChatWindow";
import MessageInput from "./MessageInput";
import { v4 as uuidv4 } from "uuid";
import { useOutletContext, useParams } from "react-router-dom";
import { transformArray } from "../helpers";
import LimitModal from "./LimitModal";
import { getAuth } from "firebase/auth";
import { AppDispatch } from "../redux/store";
import { fetchPrompts } from "../redux/slices/promptsSlice";
import useEnhancedNavigate from "./Session/helpers";
import { clearPromptSelected } from "../redux/slices/recommendedPromptsSlice";
import { setWatchlistDrawerState } from "../redux/slices/watchlistSlice";

import { logClickEvent } from "../analytics";
import Modal from "./Watchlist/Modal";
import RightSidebar from "./Sidebar/RightSidebar";
import { updateSelectedTickerProps } from "../redux/slices/newsSummarySlice";
import { openAuthModal } from "../redux/slices/userSlice";
import CustomizeSettingsModal from "./ChatMessage/CustomizeSettingsModal";
import { fetchChatPreferences, setShowSettings, setShowWarning } from "../redux/slices/responseCustomizationSlice";
import WarningModal from "./Shared/WarningModal";
import { defaultPreferences, PendingQueryType } from "./ChatMessage/constants";
import { setLastViewed } from "../redux/slices/changeLogsSlice";
import Cookies from "universal-cookie";
import ChangeLogModal from "./ChangeLogModal";
import { useQueryClient } from "@tanstack/react-query";
import { AuthActions, PaginatedChatsResponse, PaginatedRecords } from "./Sidebar/constants";
import { useMessages } from "../context/MessagesContext";
import Navbar from "./ChatWindow/Navbar";
import { useSetChatLastUpdated } from "./api/chatHistory";
import { fetchLatestMessage } from "../redux/slices/pastSessionSlice";
import { cancelChatResponse } from "../redux/slices/pastSessionSlice";

const cookies = new Cookies();
export const makeUserMessage = (text: string, attachments?: MessageAttachments, messageId?: string) => {
  return {
    chat_id: "temp" + messageId,
    isBot: false,
    output: text,
    attachments,
  };
};

// Main App component
const FinchatGPT = () => {
  const user = useSelector((state: { auth }) => state.auth.currentUser);
  const plan = useSelector((state: { plan }) => state?.plan?.plan);
  const tokenStatus = useSelector((state: { token }) => state.token.status);
  const { session_id } = useParams();
  const sessionIdSafe = session_id || uuidv4(); // Default to a new UUID if sessionId is undefined
  const finchatMessage = useSelector(
    (state: RootState) => state?.finchat?.finchatMessages[session_id],
  );
  const loading = useSelector((state: RootState) => state?.finchat?.loading);
  const { pastMessages } = useSelector((state: RootState) => state.session);

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useEnhancedNavigate();

  const auth = getAuth();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));

  const prompts = useSelector((state: RootState) => state?.prompts.prompts);
  const promptsStatus = useSelector(
    (state: RootState) => state?.prompts.status,
  );
  const { status: userValidationStatus, isValid } = useSelector(
    (state: { validUser }) => state?.validUser,
  );

  const accessToken = user?.accessToken || "";
  const user_id = user?.userId || "";

  const recommendedPrompt = useSelector(
    (state: { recommendedPrompts }) => state.recommendedPrompts.promptSelected,
  );

  const { showSettings, showWarning } = useSelector((state: { responseCustomization }) => state.responseCustomization);

  const { messages, setMessages } = useMessages();

  const [invalidChat, setInvalidChat] = useState<boolean>(false);
  const [shareModal, setShareModal] = useState<boolean>(false);

  const isUserSignedIn = useMemo(() => {
    return !!auth?.currentUser;
  }, [auth?.currentUser]);

  const [isLimitModalOpen, setIsLimitModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { isWatchlistDrawerOpen, removedIds, reorderIds } = useSelector(
    (state: { watchlist }) => state.watchlist,
  );

  const { savedSessionPreferences } = useSelector((state: { responseCustomization }) => state.responseCustomization);

  // fetch last saved pref from session id -> send message
  const preferences = savedSessionPreferences[sessionIdSafe] ?? defaultPreferences;
  const onClickWatchlistDisplayTrigger = useCallback(() => {
    if (isWatchlistDrawerOpen && (removedIds.length || reorderIds.length)) {
      setIsModalOpen(true);
    }
    else {
      dispatch(updateSelectedTickerProps(null));
      dispatch(setWatchlistDrawerState(!isWatchlistDrawerOpen));
    }
  }, [removedIds, reorderIds, isWatchlistDrawerOpen]);

  const postMessages: PostMessagesFunction = useCallback((text, input_type, token, attachments) => {
    if (text.trim() || attachments?.images?.length) {
      const chat_id = uuidv4();
      const payload: FinchatMessagePayload = {
        input: text,
        chat_id,
        session_id: sessionIdSafe,
        user_id,
        accessToken: token ? token : accessToken,
        input_type,
        preferences,
      };
      if (attachments) {
        payload.attachments = attachments;
      }
      dispatch(
        finchatMessageLoad(payload),
      );

      setIsAppendMessages(false);
      setMessages((allMessages) => {
        const prevMessages = allMessages[sessionIdSafe] || [];

        return {
          ...allMessages,
          [sessionIdSafe]: [...prevMessages, makeUserMessage(text, attachments)],
        };
      });
    }
  },
  [sessionIdSafe, accessToken, finchatMessageLoad, preferences],
  );

  const queryClient = useQueryClient();

  const setTempChatOnSidebar = (title: string, sessionId: string) => {
    if (!sessionId) return;
    const tempChat = {
      title,
      date: new Date().toISOString(),
      sessionId,
    };
    const queryKey = ["chats"];

    queryClient.setQueryData(queryKey, (oldData: PaginatedChatsResponse) => {
      if (!oldData) return oldData;

      // Check if sessionId already exists in any page
      const sessionExists = oldData.pages.some(page =>
        page.records?.some(record => record.sessionId === sessionId),
      );

      if (sessionExists) return oldData;

      const updatedFirstPage: PaginatedRecords = {
        ...oldData.pages[0],
        records: [tempChat, ...(oldData.pages[0]?.records || [])],
        pagination: {
          ...oldData.pages[0].pagination,
          totalCount: (oldData.pages[0].pagination?.totalCount || 0) + 1,
        },
      };

      return {
        ...oldData,
        pages: [updatedFirstPage, ...oldData.pages.slice(1)],
      };
    });
  };

  const handleSendMessage: sendMessageFunction = (text, input_type, attachments) => {
    if (plan.promptsLeft === 0) {
      setIsLimitModalOpen(true);
      return;
    }
    postMessages(text, input_type, undefined, attachments);
    setTempChatOnSidebar(text, sessionIdSafe);
  };

  const handlePromptSelect = (prompt: string, position: number) => {
    if (!loading && isUserSignedIn) {
      logClickEvent("homepage_prompt_click", { clicked_prompt: prompt });
      handleSendMessage(prompt, `starter_${position}`);
      return;
    }
    dispatch(openAuthModal({ type: AuthActions.SIGNIN }));
  };

  const handleCancel = (messageId) => {
    dispatch(finchatMessageCancel(session_id));
    dispatch(cancelChatResponse({ sessionId: session_id, messageId }));
  };

  useEffect(() => {
    if (recommendedPrompt) {
      handleSendMessage(recommendedPrompt, "recommend");
      dispatch(clearPromptSelected());
    }
  }, [recommendedPrompt]);

  useEffect(() => {
    if (userValidationStatus === "succeeded" && !isValid) {
      navigate("/account-deleted");
    }
  }, [userValidationStatus, isValid, navigate]);

  const [isAppendMessages, setIsAppendMessages] = useState<boolean>(false);

  useEffect(() => {
    if (!user) return;

    // if past messages belong to another chat session, don't append the messages to current chat session
    if (pastMessages[0]?.session_id !== sessionIdSafe) {
      return;
    }

    if (pastMessages.length) {
      setIsAppendMessages(false);
      const formattedPastChat = transformArray(
        pastMessages,
        user,
        setInvalidChat,
      );
      setMessages((allMessages) => {
        return {
          ...allMessages,
          [sessionIdSafe]: formattedPastChat,
        };
      });
      const lastMessage = pastMessages[pastMessages.length - 1];
      const lastChatId = lastMessage?.id;
      // check cache to see if chat session has task schedule
      const chatData = queryClient.getQueryData<PaginatedChatsResponse>(["chats"]);
      const hasTask = chatData?.pages.some(page =>
        page.records.some(record => record.sessionId === sessionIdSafe && record.task_schedule),
      );
      if (hasTask) {
        useSetChatLastUpdated({
          sessionId: sessionIdSafe,
          last_read_message_id: lastChatId,
        }).then(() => {
          queryClient.invalidateQueries({
            queryKey: ["chats"],
          });
        });
      }
    }
  }, [pastMessages, user]);

  const pendingQueries = useSelector((state: RootState) => state.session.pendingQueries);
  const currentPendingQuery = pendingQueries?.[session_id] ?? null; // current pending query
  const prevPendingQueriesRef = useRef<Record<string, PendingQueryType | null>>({});

  useEffect(() => {
    Object.entries(pendingQueries).forEach(([id, query]: [string, PendingQueryType]) => {
      if (query) {
        // setTempChatOnSidebar optimistically adds chat to sidebar (if yet to exist)
        setTempChatOnSidebar(query.user_query, id);
      }
    });
  }, [pendingQueries]);

  useEffect(() => {
    if (!session_id) return;
    const prevQuery = prevPendingQueriesRef.current[session_id];

    // only fetch latest message if a pending query just completed
    if (prevQuery && !currentPendingQuery) {
      // refetch the chat history list to get newly added one
      queryClient.invalidateQueries({
        queryKey: ["chats"],
      });
      dispatch(fetchLatestMessage({ sessionId: session_id }))
        .unwrap()
        .then((latestMessage) => {
          // get only the new messages, and append it to messages state (replace previously created temp user query)
          setMessages((prev) => {
            const prevMsgs = prev?.[session_id] || [];
            const lastMsg = prevMsgs?.[prevMsgs.length - 1];
            let base = prevMsgs;
            if (lastMsg?.chat_id?.startsWith("temp")) { // remove temp user query
              base = prevMsgs.slice(0, -1);
            }
            // handle case where there are no new messages
            if (!latestMessage || (latestMessage?.message_id === base[base.length - 1]?.chat_id)) {
              return {
                ...prev,
                [session_id]: base,
              };
            }
            const newMsgs = transformArray([latestMessage], user, setInvalidChat);
            setIsAppendMessages(true);
            return {
              ...prev,
              [session_id]: [...base, ...newMsgs],
            };
          });
        });
    }
    prevPendingQueriesRef.current[session_id] = currentPendingQuery;
  }, [currentPendingQuery, session_id]);

  useEffect(() => {
    if (
      !prompts.length
      && promptsStatus === "idle"
      && tokenStatus === "succeeded"
    ) {
      dispatch(fetchPrompts());
    }
  }, [dispatch, prompts, promptsStatus, tokenStatus]);

  const { setIsSidebarExpanded } = useOutletContext<MainLayoutType>();

  useEffect(() => {
    if (tokenStatus !== "succeeded") return;
    // when chat session changes, fetch latest preferences for that chat and store it in global state
    if (!sessionIdSafe) return;
    if (isMobile) setIsSidebarExpanded(false);
    const existingPreferences = savedSessionPreferences[sessionIdSafe];
    if (!existingPreferences) {
      dispatch(fetchChatPreferences(sessionIdSafe));
    }
  }, [sessionIdSafe, tokenStatus]);

  useEffect(() => {
    if (invalidChat) {
      // Not show error for now cause is showing it when user has changed.

      // dispatch(
      //   showNotification({
      //     message: `Unable to load conversation ${sessionIdSafe}`,
      //     severity: "error",
      //     horizontal: "right",
      //   })
      // );
      setInvalidChat(false);
      navigate(`/chat/${uuidv4()}`);
    }
  }, [invalidChat, dispatch, sessionIdSafe, navigate]);

  // insert a placeholder when bot response starts but output is still empty
  useEffect(() => {
    if (
      finchatMessage?.outputLoading
      && finchatMessage?.chat_id
      && !finchatMessage?.output?.length
    ) {
      setMessages((allMessages) => {
        const prev = allMessages[sessionIdSafe] || [];
        const alreadyExists = prev.some(
          m => m.chat_id === finchatMessage.chat_id,
        );
        if (alreadyExists) return allMessages;

        const placeholder = {
          chat_id: finchatMessage.chat_id,
          isBot: true,
          outputLoading: true,
          output: "",
        };

        return {
          ...allMessages,
          [sessionIdSafe]: [...prev, placeholder],
        };
      });
    }
  }, [finchatMessage?.outputLoading, finchatMessage?.chat_id]);

  useEffect(() => {
    if (finchatMessage?.output?.length && finchatMessage?.chat_id) {
      setMessages((allMessages) => {
        const prevMessages = allMessages[sessionIdSafe] || [];
        const existingMessageIndex = prevMessages.findIndex(
          m => m.chat_id === finchatMessage.chat_id,
        );
        // Update the existing bot response
        const sliceIndex
          = existingMessageIndex >= 0
            ? existingMessageIndex
            : prevMessages.length;
        const messageBefore = prevMessages.at(sliceIndex - 1);

        return {
          ...allMessages,
          [sessionIdSafe]: [
            ...prevMessages.slice(0, sliceIndex),
            ...(messageBefore?.isBot
              || finchatMessage.input !== messageBefore?.output
              ? [makeUserMessage(finchatMessage.input, finchatMessage.attachments)]
              : []),
            {
              ...prevMessages[sliceIndex],
              outputLoading: finchatMessage.outputLoading,
              output: finchatMessage.output,
              isBot: true,
              chart_data: finchatMessage.chart_data, // Update with new chart data
              chart_flag: finchatMessage.chart_flag, // Update the chart flag
              chat_id: finchatMessage.chat_id,
              list_of_tickers: finchatMessage.list_of_tickers,
              toolLoaders: finchatMessage.toolLoaders,
              preferences,
            },
            ...prevMessages.slice(sliceIndex + 1),
          ],
        };
      });
    }
  }, [finchatMessage]);

  const { logs: changeLogs, lastViewed } = useSelector((state: { changeLogs }) => state.changeLogs);
  const [showChangeLogModal, setShowChangeLogModal] = useState(false);
  useEffect(() => {
    if (changeLogs.length > 0 && changeLogs[0].version !== lastViewed) {
      setShowChangeLogModal(true);
    }
  }, [changeLogs, lastViewed]);

  const handleChangeLogModalClose = () => {
    setShowChangeLogModal(false);
    const version = changeLogs[0].version;
    dispatch(setLastViewed(version));
    cookies.set("changeLogs-lastViewed", version, { path: "/" });
  };

  return (
    <div className="relative z-0 flex w-full overflow-hidden h-dvh dark:bg-background/paper-elevation-1">
      {!!changeLogs.length && (
        <ChangeLogModal
          open={showChangeLogModal}
          handleClose={handleChangeLogModalClose}
          log={changeLogs[0]}
        />
      )}
      <div id="chat-portal-container" className="relative flex h-full max-w-full flex-1 flex-col overflow-hidden">
        <main className="relative h-full w-full flex-1 overflow-auto transition-width">
          <>
            <div
              role="presentation"
              className="flex h-full flex-col"
            >
              <Navbar showShareChatIcon={messages[sessionIdSafe]?.length > 0} setShareModal={setShareModal} />
              <ChatWindow
                sessionId={sessionIdSafe}
                isTablet={isTablet}
                isMobile={isMobile}
                messages={messages[sessionIdSafe]}
                isUserSignedIn={isUserSignedIn}
                shareModal={shareModal}
                setShareModal={setShareModal}
                handleSendMessage={text => postMessages(text, "user_type")}
                isAppendMessages={isAppendMessages}
              />
              <MessageInput
                sessionId={sessionIdSafe}
                handleCancel={handleCancel}
                handleSendMessage={handleSendMessage}
                setMessages={setMessages}
                showPrompts={!messages?.[sessionIdSafe]?.length}
                handlePromptSelect={handlePromptSelect}
              />
              {showSettings && (
                <div
                  className="fixed inset-0 z-50 flex items-center justify-center w-full sm:absolute sm:bottom-[13%] sm:inset-auto sm:bg-transparent bg-black/50 sm:px-6"
                >
                  <CustomizeSettingsModal
                    handleClose={() => dispatch(setShowSettings(false))}
                    sessionId={sessionIdSafe}
                  />
                </div>
              )}
              {showWarning && (
                <WarningModal
                  open={showWarning}
                  handleExit={() => {
                    dispatch(setShowWarning(false));
                    dispatch(setShowSettings(false));
                  }}
                  handleCloseWarning={() => dispatch(setShowWarning(false))}
                  mainText="Your response customization has not been saved!"
                  warningType="customization"
                />
              )}
            </div>
            <LimitModal
              open={isLimitModalOpen}
              setOpen={setIsLimitModalOpen}
              onUpgrade={() => navigate("/plans")}
              name={user?.name}
            />
          </>
        </main>
      </div>
      {/*  Watchlist is accessible only from chat page   */}
      <>
        <Tooltip
          title={`${isWatchlistDrawerOpen ? "Close" : "Open"} Watchlist`}
          placement="left"
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, -10],
                  },
                },
              ],
            },
          }}
        >
          <div
            onClick={onClickWatchlistDisplayTrigger}
            className={`${
              isMobile ? "hidden" : "block"
            } fixed right-0 top-1/2 z-40 right-sidebar-carrot  ${
              isWatchlistDrawerOpen ? "" : "closed"
            }`}
          >
            <button>
              <span>
                <div className="flex h-[88px] w-8 items-center justify-center sidebar-carrot-inner">
                  <div className="flex h-6 w-6 flex-col items-center">
                    <div className="h-3 w-1 rounded-full bg-black translator-a"></div>
                    <div className="h-3 w-1 rounded-full bg-black translator-b"></div>
                  </div>
                </div>
              </span>
            </button>
          </div>
        </Tooltip>
      </>
      {isWatchlistDrawerOpen && <RightSidebar openWatchlist={isWatchlistDrawerOpen} handlePrompt={text => postMessages(text, "news_prompt")} />}
      <Modal isOpen={isModalOpen} onCloseModal={() => setIsModalOpen(false)} />
    </div>
  );
};

export default FinchatGPT;
